
import { useState, useRef, useEffect } from 'react';

interface Service {
  title: string;
  description: string;
  icon: string;
  details: string[];
  image: string;
}

export default function CybersecurityServices() {
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const [activeService, setActiveService] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const services: Service[] = [
    {
      title: "Managed Security Services",
      description: "Complete outsourced security management with 24/7 monitoring and response",
      icon: "ri-settings-3-line",
      details: ["24/7 SOC Monitoring", "Incident Response", "Threat Hunting", "Security Analytics", "Compliance Reporting"],
      image: "https://readdy.ai/api/search-image?query=Professional%20cybersecurity%20operations%20center%20with%20multiple%20screens%20displaying%20network%20monitoring%20data%2C%20security%20analysts%20working%20at%20modern%20workstations%2C%20blue%20and%20green%20dashboard%20lights%2C%20high-tech%20environment%20with%20futuristic%20security%20interfaces%20and%20data%20visualization&width=600&height=400&seq=soc1&orientation=landscape"
    },
    {
      title: "Vulnerability Management",
      description: "Continuous assessment and remediation of security vulnerabilities",
      icon: "ri-search-eye-line",
      details: ["Automated Scanning", "Risk Assessment", "Patch Management", "Remediation Planning", "Continuous Monitoring"],
      image: "https://readdy.ai/api/search-image?query=3D%20visualization%20of%20vulnerability%20scanning%20process%20showing%20network%20topology%20with%20highlighted%20security%20weaknesses%2C%20red%20alert%20indicators%2C%20security%20assessment%20dashboard%2C%20professional%20cybersecurity%20analysis%20interface%20with%20detailed%20vulnerability%20reports%20and%20risk%20metrics&width=600&height=400&seq=vuln1&orientation=landscape"
    },
    {
      title: "Security Consulting",
      description: "Expert guidance for security strategy and implementation",
      icon: "ri-user-settings-line",
      details: ["Security Assessment", "Strategy Development", "Implementation Planning", "Training Programs", "Best Practices"],
      image: "https://readdy.ai/api/search-image?query=Professional%20cybersecurity%20consultants%20presenting%20security%20strategy%20to%20business%20executives%2C%20modern%20conference%20room%20with%20security%20diagrams%20on%20digital%20displays%2C%20professional%20business%20meeting%20environment%20with%20cybersecurity%20charts%20and%20strategic%20planning%20materials&width=600&height=400&seq=consult1&orientation=landscape"
    },
    {
      title: "Compliance Management",
      description: "Ensure adherence to regulatory standards and industry requirements",
      icon: "ri-file-shield-line",
      details: ["Regulatory Compliance", "Policy Development", "Audit Preparation", "Documentation", "Continuous Assessment"],
      image: "https://readdy.ai/api/search-image?query=Professional%20compliance%20dashboard%20showing%20regulatory%20standards%20checklist%2C%20audit%20reports%2C%20and%20certification%20badges%20including%20GDPR%2C%20HIPAA%2C%20SOX%20compliance%20indicators%2C%20organized%20legal%20documentation%20with%20security%20policy%20frameworks%20and%20compliance%20metrics&width=600&height=400&seq=comply1&orientation=landscape"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleItems(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = sectionRef.current?.querySelectorAll('.service-item');
    items?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Cybersecurity Services
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Professional cybersecurity services tailored to your organization's unique security requirements
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Services List */}
          <div className="space-y-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-index={index}
                className={`service-item group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800/60 transform transition-all duration-500 hover:scale-102 cursor-pointer ${
                  visibleItems.includes(index) ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
                } ${activeService === index ? 'border-purple-400 bg-slate-800/80' : ''}`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onClick={() => setActiveService(index)}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:animate-pulse ${
                    activeService === index ? 'animate-pulse' : ''
                  }`}>
                    <i className={`${service.icon} text-xl text-white`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-300 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {service.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-center text-sm text-gray-300">
                          <i className="ri-arrow-right-s-line text-purple-400 mr-1"></i>
                          {detail}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Active Service Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img 
                src={services[activeService].image}
                alt={services[activeService].title}
                className="w-full h-96 object-cover transform hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
              
              {/* Floating 3D elements */}
              <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-bounce">
                <i className={`${services[activeService].icon} text-white text-lg`}></i>
              </div>
            </div>

            {/* Service indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    activeService === index ? 'bg-purple-400 w-8' : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 3D Security Dashboard Model */}
        <div className="mt-16 text-center">
          <div className="relative inline-block group">
            <img 
              src="https://readdy.ai/api/search-image?query=3D%20rendered%20cybersecurity%20dashboard%20interface%20floating%20in%20space%2C%20holographic%20security%20metrics%20display%2C%20glowing%20charts%20and%20graphs%2C%20futuristic%20UI%20elements%2C%20transparent%20glass%20panels%20with%20neon%20accents%2C%20professional%20security%20analytics%20visualization%20in%20blue%20and%20purple%20colors&width=1000&height=600&seq=dashboard3d&orientation=landscape"
              alt="3D Security Dashboard"
              className="max-w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transform transition-all duration-700 cursor-pointer"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
